import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { PrintButton } from '@/components/common/PrintButton';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { TableTabs, type TableTab } from '@/components/common/TableTabs';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { donationBudgetHoldersQueryOptions } from '@/features/users/api/users.api';
import { useRevealLastViewedRow } from '@/hooks/useLastViewedRow';
import { useUrlSyncedSearch } from '@/hooks/useUrlSyncedSearch';
import { cn } from '@/lib/utils';
import { getMemberFilterField } from '@/utils/filterFields';
import { filterRowsBySearch, getTableEmptyText, type SearchableValue } from '@/utils/listSearch';
import { mpRegionProjectsQueryOptions, mpRegionsQueryOptions } from '../api/marketPromotion.api';
import { donationProjectsQueryOptions } from '../api/marketPromotionP2.api';
import { MpAreaFilterPanel } from '../components/MpAreaFilterPanel';
import { getMpRegionColumns, getMpRegionProjectColumns } from '../components/MpColumns';
import { MpRegionPrintReport } from '../components/MpRegionPrintReport';
import {
  BUDGET_HOLDER_LABEL,
  REGION_REPORT_TAB,
  type RegionReportTab,
} from '../constants/marketPromotion.constants';
import { useMpRegionFilters } from '../hooks/useMpRegionFilters';
import type { MpRegionProjectRow, MpRegionRow } from '../types/marketPromotion.types';
import { getDonationProjectsFilterField } from '../utils/mpFilterFields';
import { getRegionReportStatCards } from '../utils/regionStatCards';

const toSearchableRegion = (region: MpRegionRow): SearchableValue[] => [
  region.regionName,
  region.sideRegion,
  region.rsmName,
  region.smName,
];

const toSearchableProject = (project: MpRegionProjectRow): SearchableValue[] => [
  project.itemCode,
  project.itemName,
];

const ITEM_CODE_FIELD = {
  key: 'itemCode',
  label: 'Project Code',
  type: 'text',
  placeholder: 'Part of the code',
} as const;

export function MpRegionSummaryPage() {
  const controller = useMpRegionFilters();
  const { params, activeTab, setActiveTab } = controller;
  const regionsQuery = useQuery(mpRegionsQueryOptions(params));
  const projectsQuery = useQuery(mpRegionProjectsQueryOptions(params));
  const { data: budgetHolders } = useQuery(donationBudgetHoldersQueryOptions());
  const donationProjectsQuery = useQuery(donationProjectsQueryOptions());
  const lastViewedRsmUserId = useRevealLastViewedRow('donationRegions');
  const lastViewedProjectId = useRevealLastViewedRow('donationProjects');

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  const { appliedSearchText } = controller;
  const { searchText, setSearchText } = useUrlSyncedSearch({
    appliedText: appliedSearchText,
    onApply: controller.applySearchText,
  });
  const visibleRegions = useMemo(
    () => filterRowsBySearch(regionsQuery.data?.items, appliedSearchText, toSearchableRegion),
    [regionsQuery.data, appliedSearchText],
  );
  const visibleProjects = useMemo(
    () => filterRowsBySearch(projectsQuery.data?.items, appliedSearchText, toSearchableProject),
    [projectsQuery.data, appliedSearchText],
  );

  const isProjectsTab = activeTab === REGION_REPORT_TAB.projects;
  const activeQuery = isProjectsTab ? projectsQuery : regionsQuery;

  const tabs: TableTab<RegionReportTab>[] = [
    {
      id: REGION_REPORT_TAB.regions,
      label: 'Regions',
      count: visibleRegions.length,
      isCountLoading: regionsQuery.isLoading,
    },
    {
      id: REGION_REPORT_TAB.projects,
      label: 'Projects',
      count: visibleProjects.length,
      isCountLoading: projectsQuery.isLoading,
    },
  ];

  function renderActiveTable() {
    if (isProjectsTab) {
      return (
        <DataTable
          data={visibleProjects}
          columns={getMpRegionProjectColumns(appliedSearchText, (project) => ({
            ...params,
            projectId: project.promoItemId,
          }))}
          isLoading={projectsQuery.isLoading}
          className="lg:flex-1"
          stickyColumnId="itemName"
          {...getTableEmptyText(
            appliedSearchText,
            'No donation project handed out cash in these dates',
          )}
          isRowHighlighted={(project) => project.promoItemId === lastViewedProjectId}
        />
      );
    }
    return (
      <DataTable
        data={visibleRegions}
        columns={getMpRegionColumns(appliedSearchText, (region) => ({
          ...params,
          rsmUserId: region.rsmUserId,
        }))}
        isLoading={regionsQuery.isLoading}
        className="lg:flex-1"
        stickyColumnId="regionName"
        {...getTableEmptyText(appliedSearchText, 'No region matches')}
        isRowHighlighted={(region) => region.rsmUserId === lastViewedRsmUserId}
      />
    );
  }

  return (
    <>
      <div className={cn(FIT_TO_SCREEN_PAGE_CLASS, 'print:hidden')}>
        <PageHeader
          title="Region-Wise Market Promotion"
          titleAddon={
            <DateRangeBadge
              onClick={() => setIsFilterOpen(true)}
              dateFrom={params.dateFrom}
              dateTo={params.dateTo}
            />
          }
          search={{
            value: searchText,
            onChange: setSearchText,
            placeholder: 'Region, employee or project',
          }}
          actions={
            <>
              <AdvancedFilterButton
                isOpen={isFilterOpen}
                activeCount={controller.activeFilterCount}
                onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
              />
              <PrintButton
                disabled={regionsQuery.data === undefined || projectsQuery.data === undefined}
              />
            </>
          }
        />

        <FilterPanelCollapse isOpen={isFilterOpen}>
          <MpAreaFilterPanel
            filters={controller}
            personField={getMemberFilterField({
              key: 'budgetHolderId',
              label: BUDGET_HOLDER_LABEL,
              placeholder: 'All Budget Holders',
              members: budgetHolders,
            })}
            extraFields={[
              getDonationProjectsFilterField(
                donationProjectsQuery.data,
                donationProjectsQuery.isLoading,
              ),
              ITEM_CODE_FIELD,
            ]}
          />
        </FilterPanelCollapse>

        {activeQuery.error ? (
          <ErrorState
            error={activeQuery.error}
            fallbackTitle={
              isProjectsTab ? 'Could not load the projects' : 'Could not load the regions'
            }
            onRetry={() => void activeQuery.refetch()}
            fill
          />
        ) : (
          <>
            <StatCardGrid
              stats={getRegionReportStatCards(activeQuery.data?.totals, !isProjectsTab)}
              isLoading={activeQuery.isLoading}
            />
            <TableTabs
              tabs={tabs}
              activeTabId={activeTab}
              onTabChange={setActiveTab}
              ariaLabel="Report sections"
            />
            {renderActiveTable()}
          </>
        )}
      </div>

      <MpRegionPrintReport
        regions={regionsQuery.data}
        projects={projectsQuery.data}
        dateFrom={params.dateFrom}
        dateTo={params.dateTo}
      />
    </>
  );
}
